/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unknown-property */
import React, { FormEvent } from 'react';
import {
  Action, ActionIcon, FormHandlers, LinkOnClickEvent, Message, Portal, Spacing, useModal, useNotifications,
  Text,
  Stack,
  SimpleTable,
  ICON_CHECK,
  ICON_ALERT_CIRCLE_OUTLINE,
  Dropdown,
} from '@doveit/bricks';
import { Contact } from '../../../providers/api/dtos/contact';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import SearchContactForm, { SearchContactFormModel } from '../../components/search-contact-form/SearchContactForm';
import { matchContact } from '../../../providers/api/contact/contactProvider';
import ContactMatchCard from '../../components/contact-match-card/ContactMatchCard';
import { hasContactConflicts } from '../../utils/hasContactConflicts';
import getSearchResultTitle from './utils/getSearchResultTitle';
import NoContactView, { NoContactViewProps } from './components/no-contact-view/NoContactView';
import ViewContactPreview from '../view-contact-preview/ViewContactPreview';
import { mergeContact } from '../../utils/mergeContact';
import ContactForm, { ContactFormModel, ContactFormProps } from '../../components/contact-form/ContactForm';
import { contactFormModelToContact, contactToContactFormModel } from '../../utils/mappers';
import ContactCard from '../../components/contact-card/ContactCard';
import ContactTableRow from '../../components/contact-table-row/ContactTableRow';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { hasSamePhoneNumber } from '../../utils/hasSamePhoneNumber';
import * as styles from './SelectContact.style';
import validationSchema from '../../components/contact-form/ContactForm.schema';
import { computeResolveConflictsModalData } from './utils/computeResolveConflictsModalData';
import { MATCH_CONTACT_ERROR_MESSAGE, PREVIEW_HEADER_STATUS_CONFIG, UPDATE_WARNING_MESSAGE } from './constants';
import { computeContactStatus } from './utils/computeContactStatus';
import { ContactStatus } from './types';
import ViewContactAction from '../view-contact-action/ViewContactAction';

export interface SelectContactRef {
  resetInternalState: VoidFunction,
}

export interface ContactSelectedViewProps {
  openContactFinder: VoidFunction,
  clearContact?: VoidFunction,
  newContactValues?: Partial<Contact>,
  existingContact?: Contact,
}

export type OperationType = 'CREATE' | 'EDIT' | 'SELECT' | 'DESELECT';

export interface SelectContactProps {
  initialContact?: Contact,
  initialOverwriteValues?: Partial<Contact>,
  onContactChange: (status: ContactStatus, existingContact?: Contact, newContactValues?: Partial<Contact>, operation?: OperationType) => void,
  onContactCheck?: (status: ContactStatus) => void,
  showClearContact?: boolean,
  showBirthData?: boolean,
  showCitizenship?: boolean,
  showIdentityDocument?: boolean,
  render?: {
    noContactView?: React.FC<NoContactViewProps> | false,
    contactSelectedView?: React.FC<ContactSelectedViewProps>,
  },
  componentRef?: React.RefObject<SelectContactRef>,
  editContactRequiredFields?: ContactFormProps['requiredFields'],
  editContactSchema?: ContactFormProps['schema'],
  skipValidationOnEdit?: boolean,
}

const SelectContact: React.FC<SelectContactProps> = ({
  onContactChange,
  initialContact,
  initialOverwriteValues,
  showClearContact,
  showBirthData,
  showCitizenship,
  showIdentityDocument,
  render,
  componentRef,
  editContactRequiredFields,
  editContactSchema = validationSchema,
  skipValidationOnEdit = false,
}) => {
  const isMobile = useIsDevice('mobile');
  const searchModal = useModal();
  const resultsModal = useModal();
  const editModal = useModal();
  const resolveConflictsModal = useModal();
  const formRef = React.useRef<FormHandlers>();
  const [isSearching, setIsSearching] = React.useState(false);
  const { addError } = useNotifications();
  const [isConflictResolutionPending, setIsConflictResolutionPending] = React.useState(false);
  const [overwriteContactValues, setOverwriteContactValues] = React.useState<Partial<Contact> | undefined>(initialOverwriteValues);
  const [selectedContact, setSelectedContact] = React.useState<Contact | undefined>(initialContact);
  const [isSelected, setIsSelected] = React.useState(false);
  const [results, setResults] = React.useState<Contact[] | undefined>();
  const [potentialDuplicates, setPotentialDuplicates] = React.useState<Contact[] | undefined>();
  const [hasImportantChangesWithoutDuplicates, setHasImportantChangesWithoutDuplicates] = React.useState(false);

  const viewContact = React.useMemo(
    () => mergeContact(selectedContact, overwriteContactValues),
    [selectedContact, overwriteContactValues],
  );

  const isContactValid = React.useMemo(() => editContactSchema?.isValidSync(contactToContactFormModel(viewContact)), [editContactSchema, viewContact]);

  const status = React.useMemo(() => computeContactStatus(
    isSelected,
    isContactValid,
    isConflictResolutionPending,
    hasImportantChangesWithoutDuplicates,
    selectedContact,
    overwriteContactValues,
    potentialDuplicates,
  ), [hasImportantChangesWithoutDuplicates, isConflictResolutionPending, isContactValid, isSelected, overwriteContactValues, potentialDuplicates, selectedContact]);

  React.useEffect(() => {
    const checkDuplicateContactConflict = async () => {
      const contacts = await matchContact({
        phoneNumber: initialOverwriteValues?.phoneNumber,
        email: initialOverwriteValues?.email,
      });

      const duplicates = contacts
        .filter((contact) => contact.id !== selectedContact?.id)
        .slice()
        .sort((a, b) => ((a.name && b.name) ? a.name.localeCompare(b.name, 'it') : 0));

      setPotentialDuplicates(duplicates);
      setHasImportantChangesWithoutDuplicates(duplicates.length === 0);
      setIsConflictResolutionPending(duplicates.length > 0);
    };

    if (initialOverwriteValues && (initialOverwriteValues.email || initialOverwriteValues.phoneNumber)) {
      checkDuplicateContactConflict();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOverwriteValues]);

  React.useImperativeHandle(componentRef, () => ({
    resetInternalState: () => {
      setIsSelected(false);
      setSelectedContact(undefined);
    },
  }));

  React.useEffect(() => {
    if (initialContact) {
      setSelectedContact(initialContact);
      setIsSelected(true);
    }
  }, [initialContact]);

  React.useEffect(() => {
    if (initialOverwriteValues) {
      setOverwriteContactValues((oldOverwriteContactValues) => (
        mergeContact(oldOverwriteContactValues, initialOverwriteValues)
      ));
    }
  }, [initialOverwriteValues]);

  const onSearchClick = React.useCallback((e: LinkOnClickEvent) => {
    const formEvent = e as unknown as FormEvent<HTMLFormElement>;
    formRef.current?.handleSubmit(formEvent);
  }, []);

  const onInternalContactSelected = React.useCallback((contact?: Contact, newContactValues?: Partial<Contact>) => {
    setSelectedContact(contact);
    setPotentialDuplicates(undefined);
    setOverwriteContactValues(newContactValues);
    setIsSelected(contact !== undefined || newContactValues !== undefined);
    setHasImportantChangesWithoutDuplicates(false);

    let operation: OperationType = 'EDIT';
    if (!contact && !newContactValues) {
      operation = 'DESELECT';
    } else if (contact && !newContactValues) {
      operation = 'SELECT';
    } else if (!contact && newContactValues) {
      operation = 'CREATE';
    }

    onContactChange(status, contact, newContactValues, operation);
  }, [onContactChange, status]);

  const onResultContactSelected = React.useCallback((contact: Contact) => {
    resultsModal.close();
    onInternalContactSelected(contact);
  }, [resultsModal, onInternalContactSelected]);

  const onResultContactUpdated = React.useCallback((contact: Contact, values?: Partial<Contact>) => {
    resultsModal.close();
    onInternalContactSelected(contact, values);
  }, [resultsModal, onInternalContactSelected]);

  const [filteredResults, perfectMatch, completeResults] = React.useMemo(() => {
    if (overwriteContactValues?.email && overwriteContactValues?.phoneNumber) {
      const perfectMatchFound = results?.find(
        (contact) => contact.email === overwriteContactValues?.email
          && contact.phoneNumber === overwriteContactValues?.phoneNumber,
      );

      if (perfectMatchFound) {
        return [[perfectMatchFound], perfectMatchFound, [perfectMatchFound]];
      }
    }

    return [results, undefined, results?.filter((contact) => contact.complete)];
  }, [results, overwriteContactValues]);

  const canBeUpdated = React.useCallback(
    (result: Contact) => !perfectMatch && overwriteContactValues && hasContactConflicts(result, overwriteContactValues!),
    [perfectMatch, overwriteContactValues],
  );

  const showUpdateWarning = React.useMemo(() => filteredResults?.some((result) => canBeUpdated(result)),
    [filteredResults, canBeUpdated]);

  const onSearchContact = React.useCallback(async (values: SearchContactFormModel) => {
    try {
      setIsSearching(true);

      const newValues: Partial<Contact> = {
        name: values?.name?.trim() || undefined,
        phoneNumber: values?.phoneNumber?.trim() || undefined,
        email: values?.email?.trim() || undefined,
      };

      setOverwriteContactValues(newValues);

      const contacts = await matchContact({
        phoneNumber: newValues.phoneNumber,
        email: newValues.email,
      });

      setResults(contacts);
      searchModal.close();

      if (contacts.length === 0) {
        onInternalContactSelected(undefined, newValues);
      } else {
        resultsModal.open();
      }
    } catch (e) {
      addError(MATCH_CONTACT_ERROR_MESSAGE);
    } finally {
      setIsSearching(false);
    }
  }, [addError, searchModal, resultsModal, onInternalContactSelected]);

  const onEditContact = React.useCallback(async (values: ContactFormModel) => {
    try {
      const newValues: Partial<Contact> = contactFormModelToContact(values);
      setOverwriteContactValues(newValues);
      setPotentialDuplicates(undefined);

      const importantChanges = selectedContact?.phoneNumber !== newValues.phoneNumber || selectedContact?.email !== newValues.email;

      if (importantChanges || isConflictResolutionPending) {
        const contacts = await matchContact({
          phoneNumber: newValues.phoneNumber,
          email: newValues.email,
        });

        const duplicates = contacts
          .filter((contact) => contact.id !== selectedContact?.id)
          .slice()
          .sort((a, b) => ((a.name && b.name) ? a.name.localeCompare(b.name, 'it') : 0));

        setPotentialDuplicates(duplicates);

        setHasImportantChangesWithoutDuplicates(duplicates.length === 0);
        setIsConflictResolutionPending(duplicates.length > 0);
        resolveConflictsModal.open();
      } else {
        setHasImportantChangesWithoutDuplicates(false);
      }

      onContactChange(status, selectedContact, newValues, 'EDIT');

      editModal.close();
    } catch (e) {
      // @TODO: gestire eventuale errore di reperimento dati geografici
    }
  }, [selectedContact, isConflictResolutionPending, onContactChange, status, editModal, resolveConflictsModal]);

  const createNewContact = React.useCallback(() => {
    onInternalContactSelected(undefined, overwriteContactValues);
    setIsConflictResolutionPending(false);
    resolveConflictsModal.close();
    resultsModal.close();
  }, [onInternalContactSelected, overwriteContactValues, resolveConflictsModal, resultsModal]);

  const editContact = React.useCallback(async () => {
    editModal.open();
  }, [editModal]);

  const clearContact = React.useCallback(
    () => onInternalContactSelected(undefined, undefined),
    [onInternalContactSelected],
  );

  const resolveConflictsModalData: {
    title: string,
    description: string,
    canSubmit?: boolean,
    actions: React.ReactElement[],
    duplicates: Contact[]
  } | undefined = React.useMemo(() => computeResolveConflictsModalData(
    status,
    overwriteContactValues,
    potentialDuplicates,
    createNewContact,
    resolveConflictsModal.close,
  ), [status, overwriteContactValues, potentialDuplicates, createNewContact, resolveConflictsModal.close]);

  return (
    <>
      {!isSelected && (render?.noContactView === false
        ? null
        : (render?.noContactView
          ? render.noContactView({ openContactFinder: searchModal.open })
          : <NoContactView openContactFinder={searchModal.open} />
        )
      )}
      <div css={styles.wrapper}>
        {isSelected && (render?.contactSelectedView
          ? render.contactSelectedView({
            openContactFinder: searchModal.open,
            existingContact: selectedContact,
            newContactValues: overwriteContactValues,
            clearContact,
          })
          : (
            <ViewContactPreview
              contact={viewContact}
              oldContact={selectedContact}
              showIdentityDocument={showIdentityDocument}
              primaryActions={[
                ['DUPLICATE', 'POTENTIAL_DUPLICATE'].includes(status) && (
                  <ActionIcon
                    key="view-conflicts"
                    label="Visualizza conflitti"
                    size="S"
                    aria-label="Visualizza conflitti risolti"
                    onClick={resolveConflictsModal.open}
                    icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
                    color={status === 'DUPLICATE' ? 'critical' : 'warning'}
                  />
                ),
              ]}
              secondaryActions={[
                <Dropdown.Option
                  key="edit-contact"
                  label="Modifica contatto"
                  aria-label="Azione per modificare il contatto corrente"
                  onClick={editContact}
                />,
                selectedContact?.id && (
                  <ViewContactAction contactId={selectedContact.id}>
                    {({ onClick }) => (
                      <Dropdown.Option
                        key="view-contact"
                        label="Visualizza contatto"
                        aria-label="Azione per visualizzare la pagina del contatto"
                        onClick={onClick}
                      />
                    )}
                  </ViewContactAction>
                ),
                showClearContact && (
                  <Dropdown.Option
                    key="clear-contact"
                    label="Deseleziona contatto"
                    aria-label="Azione per deselezionare il contatto corrente"
                    onClick={() => {
                      clearContact();
                      setIsConflictResolutionPending(false);
                    }}
                  />
                ),
              ]}
              requiredFields={{
                ...editContactRequiredFields,
                birthday: !!editContactRequiredFields?.birthDate,
                birthplace: !!editContactRequiredFields?.birthPlace,
                residence: !!editContactRequiredFields?.address,
              }}
              {...PREVIEW_HEADER_STATUS_CONFIG[status]}
            />
          )
        )}
      </div>
      <Portal>
        <SimpleModal
          {...searchModal}
          title="Inserisci dati contatto"
          aria-label="Modale di ricerca contatto"
          footer={(
            <Action
              label="Aggiungi"
              dataRef="submit-contact-form"
              color="primary"
              emphasis="high"
              onClick={onSearchClick}
              loading={isSearching}
            />
          )}
        >
          <SearchContactForm
            formRef={formRef as React.MutableRefObject<FormHandlers>}
            onSubmit={onSearchContact}
            loading={isSearching}
          />
        </SimpleModal>
        {filteredResults && completeResults && (
          <SimpleModal
            {...resultsModal}
            aria-label="Modale risultati ricerca contatto"
            title={getSearchResultTitle(filteredResults.length, completeResults.length)}
            footer={(!perfectMatch && !completeResults.length) ? (
              <Action
                label="Crea nuovo"
                dataRef="create-contact-action"
                color="primary"
                emphasis="high"
                onClick={createNewContact}
              />
            ) : undefined}
          >
            {showUpdateWarning && (
              <Spacing margin={[0, 0, 200, 0]}>
                <Message
                  type="critical"
                  data-ref="update-warning-message"
                  message={UPDATE_WARNING_MESSAGE}
                />
              </Spacing>
            )}
            {filteredResults.map((result) => (
              <Spacing
                key={result.id}
                margin={[0, 0, 200, 0]}
              >
                <ContactMatchCard
                  contact={result}
                  onSelected={onResultContactSelected}
                  newContactValues={overwriteContactValues}
                  onUpdate={canBeUpdated(result) ? onResultContactUpdated : undefined}
                />
              </Spacing>
            ))}
          </SimpleModal>
        )}
        <SimpleModal
          {...editModal}
          title="Modifica contatto"
          aria-label="Modale di modifica contatto"
        >
          <ContactForm
            initialValues={contactToContactFormModel(viewContact)}
            oldInitialValues={selectedContact ? contactToContactFormModel(selectedContact) : undefined}
            showBirthData={showBirthData}
            showCitizenship={showCitizenship}
            showIdentityDocument={showIdentityDocument}
            requiredFields={editContactRequiredFields}
            onSubmit={onEditContact}
            submitLabel="Salva in bozza"
            schema={!skipValidationOnEdit ? editContactSchema : undefined}
          />
        </SimpleModal>
        {resolveConflictsModalData && (
          <SimpleModal
            {...resolveConflictsModal}
            title={resolveConflictsModalData.title}
            aria-label="Modale di risoluzione conflitti"
            footer={resolveConflictsModalData.actions && resolveConflictsModalData.actions.length > 0 ? (
              <>
                {resolveConflictsModalData.actions.map((action) => action)}
              </>
            ) : undefined}
          >
            <Stack gap={300}>
              <Text.Body>
                {resolveConflictsModalData.description}
              </Text.Body>

              {resolveConflictsModalData.duplicates.length > 0 && (
                <>
                  {isMobile && (
                    <Stack aria-label="Lista suggerimenti">
                      {resolveConflictsModalData.duplicates.map((contact) => (
                        <ContactCard
                          key={contact.id}
                          contact={contact}
                          highlight={{
                            fields: {
                              email: overwriteContactValues?.email === contact.email,
                              phoneNumber: overwriteContactValues ? hasSamePhoneNumber(overwriteContactValues, contact) : false,
                            },
                            color: 'critical.default.low',
                          }}
                          primaryAction={(
                            <ActionIcon
                              size="S"
                              icon={{ path: ICON_CHECK }}
                              label="Seleziona suggerimento"
                              aria-label="Seleziona suggerimento"
                              onClick={() => {
                                setIsConflictResolutionPending(false);
                                resolveConflictsModal.close();
                                onInternalContactSelected(contact, undefined);
                              }}
                            />
                          )}
                        />
                      ))}
                    </Stack>
                  )}

                  {!isMobile && (
                    <SimpleTable aria-label="Lista suggerimenti">
                      <SimpleTable.Body>
                        {resolveConflictsModalData.duplicates.map((contact) => (
                          <SimpleTable.Row
                            key={contact.id}
                            asChild
                          >
                            <ContactTableRow
                              contact={contact}
                              highlight={{
                                fields: {
                                  email: overwriteContactValues?.email === contact.email,
                                  phoneNumber: overwriteContactValues ? hasSamePhoneNumber(overwriteContactValues, contact) : false,
                                },
                                color: 'critical.default.low',
                              }}
                              primaryAction={(
                                <ActionIcon
                                  size="S"
                                  icon={{ path: ICON_CHECK }}
                                  label="Seleziona suggerimento"
                                  aria-label="Seleziona suggerimento"
                                  onClick={() => {
                                    setIsConflictResolutionPending(false);
                                    resolveConflictsModal.close();
                                    onInternalContactSelected(contact, undefined);
                                  }}
                                />
                              )}
                            />
                          </SimpleTable.Row>
                        ))}
                      </SimpleTable.Body>
                    </SimpleTable>
                  )}
                </>
              )}
            </Stack>
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default SelectContact;
